import { calculateElectrification } from "./calculateElectrification.ts";
import { SolutionMap, SolutionOptionKey } from "@labs/shared/types/Solution.ts";
import {
  dieselConversionFactor,
  fuelOilConversionFactor,
  gasolineConversionFactor,
  mobileElectrificationCOP,
  naturalGasConversionFactor,
  propaneConversionFactor,
  stationaryElectrificationCOP,
} from "../../data/planAnalysisConstants.ts";
import { FuelSource } from "@labs/shared/types/FuelSource.ts";
import {
  EfficiencyResultsByFuel,
  ElectrificationResultsByFuel,
  NullablePlanComponent,
  PlanComponentCategory,
} from "@labs/shared/types/AnalyzedPlan.ts";
import { combineFuelImpacts } from "./combineFuelImpacts.ts";
import { filterFuelImpacts } from "./filterFuelImpacts.ts";

export const getElectrificationComponents = (
  solutionMap: SolutionMap,
  efficiencyResultsByFuel: EfficiencyResultsByFuel,
): {
  stationaryElectrificationComponent: NullablePlanComponent;
  mobileElectrificationComponent: NullablePlanComponent;
  electrificationResultsByFuel: ElectrificationResultsByFuel;
} => {
  const stationaryElectrificationNaturalGasResults = calculateElectrification(
    solutionMap,
    SolutionOptionKey.StationaryElectrificationNaturalGas,
    efficiencyResultsByFuel.naturalGas.unitsOfEnergyAfterReduction,
    naturalGasConversionFactor,
    stationaryElectrificationCOP,
    FuelSource.NaturalGas,
  );
  const stationaryElectrificationPropaneResults = calculateElectrification(
    solutionMap,
    SolutionOptionKey.StationaryElectrificationPropane,
    efficiencyResultsByFuel.propane.unitsOfEnergyAfterReduction,
    propaneConversionFactor,
    stationaryElectrificationCOP,
    FuelSource.Propane,
  );
  const stationaryElectrificationFuelOilResults = calculateElectrification(
    solutionMap,
    SolutionOptionKey.StationaryElectrificationFuelOil,
    efficiencyResultsByFuel.fuelOil.unitsOfEnergyAfterReduction,
    fuelOilConversionFactor,
    stationaryElectrificationCOP,
    FuelSource.FuelOil,
  );

  const mobileElectrificationGasolineResults = calculateElectrification(
    solutionMap,
    SolutionOptionKey.MobileElectrificationGasoline,
    efficiencyResultsByFuel.gasoline.unitsOfEnergyAfterReduction,
    gasolineConversionFactor,
    mobileElectrificationCOP,
    FuelSource.Gasoline,
  );
  const mobileElectrificationDieselResults = calculateElectrification(
    solutionMap,
    SolutionOptionKey.MobileElectrificationDiesel,
    efficiencyResultsByFuel.diesel.unitsOfEnergyAfterReduction,
    dieselConversionFactor,
    mobileElectrificationCOP,
    FuelSource.Diesel,
  );

  const stationaryElectrificationComponent: NullablePlanComponent =
    stationaryElectrificationNaturalGasResults.solutionPercent > 0 ||
    stationaryElectrificationPropaneResults.solutionPercent > 0 ||
    stationaryElectrificationFuelOilResults.solutionPercent > 0
      ? {
          planComponentCategory: PlanComponentCategory.ElectrifyStationary,
          intermediateCostScore:
            stationaryElectrificationNaturalGasResults.costScore +
            stationaryElectrificationPropaneResults.costScore +
            stationaryElectrificationFuelOilResults.costScore,
          carbonImpact:
            stationaryElectrificationNaturalGasResults.carbonImpact +
            stationaryElectrificationPropaneResults.carbonImpact +
            stationaryElectrificationFuelOilResults.carbonImpact,
          fuelImpacts: filterFuelImpacts(
            combineFuelImpacts([
              ...stationaryElectrificationNaturalGasResults.fuelImpacts,
              ...stationaryElectrificationPropaneResults.fuelImpacts,
              ...stationaryElectrificationFuelOilResults.fuelImpacts,
            ]),
          ),
        }
      : null;

  const mobileElectrificationComponent: NullablePlanComponent =
    mobileElectrificationGasolineResults.solutionPercent > 0 ||
    mobileElectrificationDieselResults.solutionPercent > 0
      ? {
          planComponentCategory: PlanComponentCategory.ElectrifyMobile,
          intermediateCostScore:
            mobileElectrificationGasolineResults.costScore +
            mobileElectrificationDieselResults.costScore,
          carbonImpact:
            mobileElectrificationGasolineResults.carbonImpact +
            mobileElectrificationDieselResults.carbonImpact,
          fuelImpacts: combineFuelImpacts([
            ...mobileElectrificationGasolineResults.fuelImpacts,
            ...mobileElectrificationDieselResults.fuelImpacts,
          ]),
        }
      : null;
  return {
    stationaryElectrificationComponent,
    mobileElectrificationComponent,
    electrificationResultsByFuel: {
      [FuelSource.NaturalGas]: stationaryElectrificationNaturalGasResults,
      [FuelSource.Propane]: stationaryElectrificationPropaneResults,
      [FuelSource.FuelOil]: stationaryElectrificationFuelOilResults,
      [FuelSource.Diesel]: mobileElectrificationDieselResults,
      [FuelSource.Gasoline]: mobileElectrificationGasolineResults,
    },
  };
};
